import { Capacitor } from '@capacitor/core';
import api from '../api/client';

// Face ID / fingerprint as a real login (docs/TODO.md v1.0.2 §4). Distinct from
// biometricAuth.js, which only re-locks an existing session and stores nothing.
//
// The server issues one token per enrolled device (server/routes/biometricAuth.js). It lives in a
// Keychain/Keystore item created with BIOMETRY_CURRENT_SET, so the OS itself refuses to release it
// without Face ID or a strong fingerprint, and adding a new face or finger invalidates it. Read it
// ONLY through getSecureCredentials: getCredentials reads the unbound store, and setting
// authValidityDuration would let reads skip the prompt for a while. Both defeat the point.
//
// The token rotates on every sign-in. On Android, writing the new one back shows a second prompt
// (owner decision 2026-10-02: single-use tokens are worth it). If that write fails, the device is
// forgotten: keeping the rotated-out token would make the next sign-in look like a copied token.
//
// This is convenience at the phone-unlock boundary, not a stronger factor than TOTP. Enrolling
// therefore needs a current TOTP code when 2FA is on.

const SERVER = 'agence.finance.biometric-login';
// The device id is not a secret. It is cached here so "forget this device" needs no Face ID
// prompt. The Keychain item stays authoritative: on iOS it survives a reinstall that clears this.
const DEVICE_KEY = 'agence_biometric_device_id';
const BIOMETRY_CURRENT_SET = 1;
const NO_PROTECTED_ITEM = new Set(['21', '0']); // 0: Android's invalidated key after an enrollment change

// Never return a Capacitor plugin proxy from an async function: its `then` forwards to native
// and the await hangs. Same rule as biometricAuth.js's loadPlugin(); see its comment.
async function loadPlugin() {
  const mod = await import('@capgo/capacitor-native-biometric');
  return { plugin: mod.NativeBiometric };
}

function platformLabel() {
  return Capacitor.getPlatform() === 'ios' ? 'iPhone' : 'Android phone';
}

function store(plugin, deviceId, token) {
  return plugin.setCredentials({ server: SERVER, username: deviceId, password: token, accessControl: BIOMETRY_CURRENT_SET });
}

async function clearLocal(plugin) {
  localStorage.removeItem(DEVICE_KEY);
  try { await plugin.deleteCredentials({ server: SERVER }); } catch { /* nothing stored */ }
}

export function currentDeviceId() {
  return localStorage.getItem(DEVICE_KEY);
}

/** Whether this device can use biometric sign-in at all (native, with biometrics enrolled). */
export async function isBiometricLoginAvailable() {
  if (!Capacitor.isNativePlatform()) return false;
  try {
    const { plugin } = await loadPlugin();
    // Strong (Class 3) only: the stored key requires it. Android also reports weak biometrics
    // (face unlock on many phones) as available, where enrolling would then fail. Fingerprint
    // sensors are strong on essentially every phone; iOS Face ID and Touch ID are always strong.
    const result = await plugin.isAvailable();
    return !!(result.isAvailable && result.strongBiometryIsAvailable);
  } catch {
    return false;
  }
}

/**
 * Whether biometric sign-in is on for this device, as far as this install knows. No prompt.
 *
 * Deliberately NOT the plugin's isCredentialsSaved(): on iOS it queries the Face ID-protected
 * Keychain item without suppressing UI, so merely checking shows a Face ID prompt (Settings
 * prompted twice on every visit, 2026-10-02). The device-id cache is written on every enroll and
 * successful sign-in. After an iOS reinstall it is empty while the Keychain item survives; the
 * Login screen covers that by always trying signInWithBiometrics() on tap, which fails fast with
 * no prompt when nothing is stored.
 */
export async function hasBiometricLogin() {
  return Capacitor.isNativePlatform() && !!currentDeviceId();
}

/**
 * Turns on biometric sign-in for this device. Needs a session. With 2FA on it also needs either
 * `code` (the current TOTP code) or `enrollToken` (the 10-minute pass a fresh sign-in returns).
 * Resolves { ok: true } or { ok: false, requiresTotp?, error }. Never rejects.
 */
export async function enrollBiometricLogin({ code, enrollToken } = {}) {
  let enrolled;
  try {
    const { data } = await api.post('/auth/biometric/enroll', { platform: Capacitor.getPlatform(), label: platformLabel(), code, enrollToken });
    enrolled = data;
  } catch (err) {
    const body = err?.response?.data || {};
    return { ok: false, requiresTotp: !!body.requiresTotp, error: body.error || 'Could not turn on biometric sign-in' };
  }
  const { plugin } = await loadPlugin();
  try {
    await store(plugin, enrolled.deviceId, enrolled.token);
  } catch {
    // The phone declined to store it (prompt cancelled, no biometrics): undo the server side.
    try { await api.delete(`/auth/biometric/devices/${enrolled.deviceId}`); } catch { /* expires unused */ }
    return { ok: false, error: 'Biometric sign-in was not turned on.' };
  }
  localStorage.setItem(DEVICE_KEY, enrolled.deviceId);
  return { ok: true };
}

/**
 * Signs in with Face ID / fingerprint. Resolves { ok: true, token, userId, forgotten? } or
 * { ok: false, reason: 'cancelled' | 'unavailable' | 'revoked' | 'invalid' | 'network' }. Never rejects.
 */
export async function signInWithBiometrics() {
  if (!Capacitor.isNativePlatform()) return { ok: false, reason: 'unavailable' };
  const { plugin } = await loadPlugin();

  let creds;
  try {
    creds = await plugin.getSecureCredentials({ server: SERVER, reason: 'Sign in to Agence', title: 'Sign in to Agence', negativeButtonText: 'Use password' });
  } catch (err) {
    if (NO_PROTECTED_ITEM.has(String(err?.code))) {
      await clearLocal(plugin);
      return { ok: false, reason: 'unavailable' };
    }
    return { ok: false, reason: 'cancelled' };
  }

  let data;
  try {
    ({ data } = await api.post('/auth/biometric/login', { deviceId: creds.username, token: creds.password }));
  } catch (err) {
    if (!err?.response) return { ok: false, reason: 'network' }; // token not consumed; keep it
    await clearLocal(plugin);
    return { ok: false, reason: err.response.data?.revoked ? 'revoked' : 'invalid' };
  }

  try {
    await store(plugin, creds.username, data.biometricToken);
    localStorage.setItem(DEVICE_KEY, creds.username);
    return { ok: true, token: data.token, userId: data.userId };
  } catch {
    await clearLocal(plugin);
    try {
      await api.delete(`/auth/biometric/devices/${creds.username}`, { headers: { Authorization: `Bearer ${data.token}` } });
    } catch { /* the row expires unused */ }
    return { ok: true, token: data.token, userId: data.userId, forgotten: true };
  }
}

/** "Sign out and forget this device": revoke server-side, then delete the item. No prompt. */
export async function forgetBiometricLogin() {
  const deviceId = currentDeviceId();
  if (deviceId) {
    try { await api.delete(`/auth/biometric/devices/${deviceId}`); } catch { /* still clear locally */ }
  }
  if (!Capacitor.isNativePlatform()) {
    localStorage.removeItem(DEVICE_KEY);
    return;
  }
  const { plugin } = await loadPlugin();
  await clearLocal(plugin);
}
